// src/components/LanguageSwitcher.tsx
// Bouton de basculement de langue FR ↔ EN — intégrable dans toute la nav
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

interface Props {
  compact?: boolean; // true = icône seule (mobile), false = icône + label
}

const LanguageSwitcher: React.FC<Props> = ({ compact = false }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'fr';

  // Fermer si clic extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const switchTo = (lang: 'fr' | 'en') => {
    i18n.changeLanguage(lang);
    localStorage.setItem('sagecom_lang', lang);
    setOpen(false);
  };

  const flags: Record<string, string> = { fr: '🇫🇷', en: '🇬🇧' };
  const labels: Record<string, string> = { fr: 'FR', en: 'EN' };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        title={t('lang.switch')}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors text-sm font-medium"
      >
        <Globe className="w-4 h-4 flex-shrink-0" />
        {!compact && (
          <span className="flex items-center gap-1">
            <span>{flags[currentLang]}</span>
            <span className="hidden sm:inline">{labels[currentLang]}</span>
          </span>
        )}
        {compact && <span className="text-xs font-bold">{labels[currentLang]}</span>}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-36 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-[9999]">
          {(['fr', 'en'] as const).map((lang) => (
            <button
              key={lang}
              onClick={() => switchTo(lang)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                currentLang === lang
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-base">{flags[lang]}</span>
              <span>{t(`lang.${lang}`)}</span>
              {currentLang === lang && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
